import { Alert, Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';

import { compactDiff, computeDiff, type DiffLine } from './diff';
import { COLORS } from './ui';

const stringify = (value: unknown) =>
  value == null
    ? ''
    : typeof value === 'string'
    ? value
    : JSON.stringify(value, null, 2);

function Line({ line }: { line: DiffLine }) {
  const marker = line.type === 'add' ? '+' : line.type === 'del' ? '-' : ' ';
  const bg =
    line.type === 'add'
      ? `${COLORS.green}18`
      : line.type === 'del'
      ? `${COLORS.red}18`
      : 'transparent';
  const color =
    line.type === 'add'
      ? COLORS.green
      : line.type === 'del'
      ? COLORS.red
      : COLORS.text;
  return (
    <Box component="div" sx={{ display: 'flex', bgcolor: bg }}>
      <Typography
        component="span"
        sx={{
          width: 18,
          flexShrink: 0,
          color,
          fontFamily: 'monospace',
          fontSize: 11,
          textAlign: 'center',
          userSelect: 'none',
        }}
      >
        {marker}
      </Typography>
      <Typography
        component="span"
        sx={{
          color,
          fontFamily: 'monospace',
          fontSize: 11,
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
        }}
      >
        {line.text || ' '}
      </Typography>
    </Box>
  );
}

export function DiffView({
  before,
  after,
}: {
  before: unknown;
  after: unknown;
}) {
  const [mode, setMode] = useState<'compact' | 'full'>('compact');
  const lines = useMemo(
    () => computeDiff(stringify(before), stringify(after)),
    [before, after],
  );
  const changed = useMemo(
    () => lines.some(line => line.type !== 'same'),
    [lines],
  );
  const chunks = useMemo(
    () =>
      mode === 'compact'
        ? compactDiff(lines)
        : [{ kind: 'lines' as const, lines }],
    [lines, mode],
  );

  if (!changed) {
    return <Alert severity="success">Live state matches desired state</Alert>;
  }

  return (
    <Box>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={mode}
        onChange={(_event, value) => value && setMode(value)}
        sx={{ mb: 1 }}
      >
        <ToggleButton value="compact" sx={{ px: 1.5, py: 0.25, fontSize: 11 }}>
          Compact
        </ToggleButton>
        <ToggleButton value="full" sx={{ px: 1.5, py: 0.25, fontSize: 11 }}>
          Full
        </ToggleButton>
      </ToggleButtonGroup>
      <Box
        sx={{
          p: 1,
          maxHeight: 520,
          overflow: 'auto',
          border: `1px solid ${COLORS.border}`,
          borderRadius: 1,
          bgcolor: '#0b1020',
        }}
      >
        {chunks.map((chunk, index) =>
          chunk.kind === 'collapsed' ? (
            <Typography
              key={index}
              sx={{
                py: 0.5,
                color: COLORS.muted,
                fontFamily: 'monospace',
                fontSize: 11,
                textAlign: 'center',
                bgcolor: `${COLORS.border}30`,
              }}
            >
              ⋯ {chunk.count} unchanged line{chunk.count === 1 ? '' : 's'} ⋯
            </Typography>
          ) : (
            <Box key={index}>
              {chunk.lines.map((line, lineIndex) => (
                <Line key={lineIndex} line={line} />
              ))}
            </Box>
          ),
        )}
      </Box>
    </Box>
  );
}
